import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";

import type { MediaUploadResult } from "../types";

export type { MediaUploadResult };

/** 上传过程通知及取消控制；不改变既有调用方的默认请求策略。 */
export type MediaUploadOptions = {
  purpose?: "contact_record";
  onProgress?: (percent: number) => void;
  signal?: AbortSignal;
};

/** 通用媒体上传（七牛），返回公开 URL。 */
export async function uploadMedia(
  file: File,
  kind: "image" | "file" = "image",
  options: MediaUploadOptions = {},
): Promise<MediaUploadResult> {
  const form = new FormData();
  form.append("file", file);
  const purposeQuery = options.purpose ? `&purpose=${options.purpose}` : "";
  const response = await httpClient.post<
    ApiEnvelope<{ key: string; file_url: string; media_id?: string | null }>
  >(`/media/upload?kind=${encodeURIComponent(kind)}${purposeQuery}`, form, {
    "axios-retry": { retries: 0 },
    // 150MB 视频须覆盖弱网上传和服务端转存；仅联系人用途延长等待，仍支持取消。
    ...(options.purpose === "contact_record" ? { timeout: 600_000 } : {}),
    signal: options.signal,
    onUploadProgress: options.onProgress
      ? ({ loaded, total }) => {
          if (total) options.onProgress?.(Math.min(100, Math.round(loaded * 100 / total)));
        }
      : undefined,
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("媒体上传接口返回了无效数据");
  }
  const data = response.data.data;
  return {
    key: data.key,
    fileUrl: data.file_url,
    mediaId: data.media_id ?? data.key,
  };
}

/** 按 key 或完整 URL 删除七牛对象；失败由调用方决定是否忽略。 */
export async function deleteMedia(params: { key?: string; url?: string }): Promise<void> {
  const response = await httpClient.post<ApiEnvelope<{ deleted: boolean }>>(
    "/media/files/delete",
    {
      key: params.key?.trim() || undefined,
      url: params.url?.trim() || undefined,
    },
    { "axios-retry": { retries: 0 } },
  );
  if (response.data.code !== "OK") {
    throw new Error("媒体删除接口返回了无效数据");
  }
}
